import { GAMEPLAY_CATALOG_VERSION, getGameplayCapability, validateCapabilityConfig, type GameplayCapabilityId } from './catalog.js';
import { canonicalDigest, contractError, freezeJson, validateJsonObject, validateSha256, type JsonObject } from './json.js';

export interface CapabilitySelection {
  id: GameplayCapabilityId;
  version: number;
  config: JsonObject;
  basis: 'creator' | 'analyzer' | 'legacy-explicit';
}
export interface GameplayCapabilityLockV1 {
  schemaVersion: 1;
  catalogVersion: string;
  capabilities: CapabilitySelection[];
  digest: string;
}

export function validateCapabilitySelection(value: unknown): CapabilitySelection {
  const v = validateJsonObject(value, 'capability');
  for (const k of Object.keys(v)) if (!['id', 'version', 'config', 'basis'].includes(k)) contractError(`capability.${k}`, 'unsupported field');
  if (!Object.hasOwn(v, 'version')) contractError('capability.version', 'required; lock an explicit supported version');
  getGameplayCapability(v.id, v.version);
  if (!['creator', 'analyzer', 'legacy-explicit'].includes(v.basis as string)) contractError('capability.basis', 'expected creator, analyzer or legacy-explicit');
  return { id: v.id as GameplayCapabilityId, version: v.version as number,
    config: validateCapabilityConfig(v.id as GameplayCapabilityId, v.version as number, v.config), basis: v.basis as CapabilitySelection['basis'] };
}

export function validateGameplayCapabilityLock(value: unknown): GameplayCapabilityLockV1 {
  const v = validateJsonObject(value, 'capabilityLock');
  if (v.schemaVersion !== 1) contractError('capabilityLock.schemaVersion', `unsupported version ${String(v.schemaVersion)}; supported: 1`);
  if (v.catalogVersion !== GAMEPLAY_CATALOG_VERSION) contractError('capabilityLock.catalogVersion', `unsupported catalog ${String(v.catalogVersion)}; supported: ${GAMEPLAY_CATALOG_VERSION}`);
  for (const k of Object.keys(v)) if (!['schemaVersion', 'catalogVersion', 'capabilities', 'digest'].includes(k)) contractError(`capabilityLock.${k}`, 'unsupported field');
  if (!Array.isArray(v.capabilities) || v.capabilities.length > 64) contractError('capabilityLock.capabilities', 'expected at most 64 selections');
  const capabilities = v.capabilities.map(validateCapabilitySelection);
  for (let i = 1; i < capabilities.length; i++) if (capabilities[i - 1]!.id >= capabilities[i]!.id) contractError('capabilityLock.capabilities', 'IDs must be unique and sorted by code-unit order');
  return { schemaVersion: 1, catalogVersion: GAMEPLAY_CATALOG_VERSION, capabilities, digest: validateSha256(v.digest, 'capabilityLock.digest') };
}

export async function createGameplayCapabilityLock(selections: readonly CapabilitySelection[]): Promise<GameplayCapabilityLockV1> {
  const capabilities = selections.map(validateCapabilitySelection).sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  const body = { schemaVersion: 1 as const, catalogVersion: GAMEPLAY_CATALOG_VERSION, capabilities };
  const lock = validateGameplayCapabilityLock({ ...body, digest: await canonicalDigest(body) });
  freezeJson(lock);
  return lock;
}

/** Trusted boundary: structural validation alone does not authenticate a lock. */
export async function verifyGameplayCapabilityLock(value: unknown): Promise<GameplayCapabilityLockV1> {
  const lock = validateGameplayCapabilityLock(value);
  const { digest, ...body } = lock;
  if (await canonicalDigest(body) !== digest) contractError('capabilityLock.digest', 'content digest mismatch; rebuild from reviewed selections');
  freezeJson(lock);
  return lock;
}
